'use client';

import { useMemo } from 'react';
import { keepPreviousData, useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useCellServiceSession } from '@/hooks/useCellServiceSession';
import {
  mobileGraphql,
  mobileKeys,
  type MobileConnection,
  type MobileFrontEndGalleryView,
  type MobileUiConfigArtifact,
} from '@/shared/mobile-service/api/cors-mobile-api';
import {
  buildGalleryQuery,
  mapGalleryItems,
  withCollectedAttachmentPreview,
  type GalleryItem,
} from '../lib/build-gallery-query';

export interface GalleryPage {
  items: GalleryItem[];
  total: number;
  /** Rows returned by the page, before rows without a usable URL were dropped. */
  scanned: number;
}

/** One page of media tiles for a gallery view. */
export function useGalleryItems(args: {
  mobileAcquisitionId: number;
  graphqlType: string;
  artifact: MobileUiConfigArtifact;
  view: MobileFrontEndGalleryView;
  offset: number;
  limit: number;
  where: Record<string, unknown> | null;
  /** When false the query stays idle. Defaults to on. */
  active?: boolean;
}): UseQueryResult<GalleryPage, Error> {
  const { mobileAcquisitionId, graphqlType, artifact, view, offset, limit, where } = args;
  const preview = useMemo(
    () => withCollectedAttachmentPreview(view, artifact),
    [view, artifact],
  );
  const { cellId, accessToken, enabled } = useCellServiceSession();

  const rootQuery = artifact.front_end?.graphql_root_query ?? null;

  const query = useMemo(() => {
    if (!rootQuery) return null;
    return buildGalleryQuery({
      rootQuery,
      view: preview,
      artifact,
      mobileAcquisitionId,
      offset,
      limit,
      where,
    });
  }, [rootQuery, preview, artifact, mobileAcquisitionId, offset, limit, where]);

  return useQuery<GalleryPage, Error>({
    queryKey: mobileKeys.artifactList(cellId, mobileAcquisitionId, graphqlType, {
      view: 'gallery',
      offset,
      limit,
      where,
    }),
    enabled: enabled && args.active !== false && Boolean(query),
    placeholderData: keepPreviousData,
    queryFn: async ({ signal }) => {
      const data = await mobileGraphql<
        Record<string, MobileConnection<Record<string, unknown>>>
      >({
        cellId: cellId as string,
        accessToken: accessToken as string,
        endpoint: artifact.endpoint,
        query: query as string,
        signal,
      });
      const connection = data[rootQuery as string];
      const rows = connection?.items ?? [];
      return {
        items: mapGalleryItems(rows, preview),
        total: connection?.total ?? 0,
        scanned: rows.length,
      };
    },
  });
}
